import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { getFlowSteps } from '@/data/journeyFlows';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JourneyContext = createContext();

const getDeviceId = () => {
  let deviceId = localStorage.getItem('vindicated-device-id');
  if (!deviceId) {
    deviceId = 'device-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('vindicated-device-id', deviceId);
  }
  return deviceId;
};

// Storage key for journey progress persistence
const JOURNEY_STORAGE_KEY = 'vindicated_journey_progress_v1';

export const JourneyProvider = ({ children }) => {
  const [currentPath, setCurrentPath] = useState(null); // 'buy', 'lease', 'trade', 'learn'
  const [currentStep, setCurrentStep] = useState(0);
  const [journeyData, setJourneyData] = useState({});
  const [savingsData, setSavingsData] = useState({
    total: 0,
    breakdown: [],
    visible: false,
    expanded: false,
    hasShownExpanded: false
  });
  const [deviceId] = useState(getDeviceId);
  
  // Visited steps history stack - tracks step IDs in visit order
  const [visitedSteps, setVisitedSteps] = useState([]);
  
  // Flag to indicate if there's saved progress to resume
  const [hasSavedProgress, setHasSavedProgress] = useState(false);
  
  // Full journey access - SERVER AUTHORITATIVE (not localStorage)
  const [hasFullJourneyAccess, setHasFullJourneyAccessState] = useState(false);
  const [entitlementLoading, setEntitlementLoading] = useState(true);
  const [entitlementError, setEntitlementError] = useState(null);

  // Check entitlement from server
  const checkEntitlementFromServer = useCallback(async () => {
    const currentDeviceId = typeof deviceId === 'function' ? deviceId() : deviceId;
    if (!currentDeviceId) {
      setEntitlementLoading(false);
      return;
    }
    
    try {
      setEntitlementLoading(true);
      setEntitlementError(null);
      
      const response = await axios.get(`${API}/entitlements/full-journey`, {
        params: { device_id: currentDeviceId },
        validateStatus: (status) => status === 200 || status === 402
      });
      
      if (response.status === 200 && response.data.has_full_journey_access) {
        setHasFullJourneyAccessState(true);
      } else {
        // 402 or no access
        setHasFullJourneyAccessState(false);
      }
    } catch (error) {
      console.error('Entitlement check failed:', error);
      setEntitlementError(error);
      setHasFullJourneyAccessState(false);
    } finally {
      setEntitlementLoading(false);
    }
  }, [deviceId]);

  // Check entitlement on mount
  useEffect(() => {
    checkEntitlementFromServer();
  }, [checkEntitlementFromServer]);

  // Setter that triggers server re-check (does NOT directly set true)
  const refreshEntitlement = useCallback(async () => {
    await checkEntitlementFromServer();
  }, [checkEntitlementFromServer]);

  // Load journey progress from localStorage on mount
  useEffect(() => {
    // Try new storage key first, then fall back to legacy key
    let saved = localStorage.getItem(JOURNEY_STORAGE_KEY);
    if (!saved) {
      saved = localStorage.getItem('vindicated-journey');
    }
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        
        // Only restore if there's a valid path and step
        if (parsed.currentPath && typeof parsed.currentStep === 'number') {
          setCurrentPath(parsed.currentPath);
          setCurrentStep(parsed.currentStep || 0);
          setJourneyData(parsed.journeyData || {});
          setSavingsData(parsed.savingsData || {
            total: 0,
            breakdown: [],
            visible: false,
            expanded: false,
            hasShownExpanded: false
          });
          
          // Load visitedSteps if available, otherwise initialize from current step
          if (parsed.visitedSteps && Array.isArray(parsed.visitedSteps)) {
            setVisitedSteps(parsed.visitedSteps);
          } else {
            // Reconstruct visitedSteps from currentStep for backwards compatibility
            const steps = getFlowSteps(parsed.currentPath);
            if (steps && steps[parsed.currentStep]) {
              setVisitedSteps([steps[parsed.currentStep].id]);
            }
          }
          
          // Mark that we have saved progress to resume
          setHasSavedProgress(true);
        }
      } catch (e) {
        console.error('Failed to load journey data:', e);
      }
    }
  }, []);

  // Save journey progress to localStorage
  const saveProgress = useCallback((updates) => {
    const data = {
      currentPath,
      currentStep,
      journeyData,
      savingsData,
      visitedSteps,
      ...updates
    };
    
    try {
      localStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(data));
      // Also save to legacy key for compatibility
      localStorage.setItem('vindicated-journey', JSON.stringify(data));
      // Mark that we now have saved progress
      setHasSavedProgress(true);
    } catch (e) {
      // fail silently; do not block the app
    }
    
    // Optional: sync to backend for analytics
    try {
      axios.post(`${API}/journey-progress`, {
        deviceId,
        ...data,
        timestamp: new Date().toISOString()
      }).catch(err => console.error('Failed to sync journey:', err));
    } catch (e) {
      // Silent fail for analytics
    }
  }, [currentPath, currentStep, journeyData, savingsData, visitedSteps, deviceId]);

  const startPath = (path) => {
    // Clear any existing saved progress when starting a fresh journey
    try {
      localStorage.removeItem(JOURNEY_STORAGE_KEY);
      localStorage.removeItem('vindicated-journey');
    } catch (e) {
      // fail silently
    }
    
    // Reset savings when starting a new journey
    const resetSavings = {
      total: 0,
      breakdown: [],
      visible: false,
      expanded: false,
      hasShownExpanded: false
    };
    // Initialize visitedSteps with the first step ID
    const steps = getFlowSteps(path);
    const firstStepId = steps && steps[0] ? steps[0].id : null;
    const initialVisitedSteps = firstStepId ? [firstStepId] : [];
    
    const updates = { 
      currentPath: path, 
      currentStep: 0,
      journeyData: {}, // Reset journey data
      savingsData: resetSavings,
      visitedSteps: initialVisitedSteps
    };
    setCurrentPath(path);
    setCurrentStep(0);
    setJourneyData({});
    setSavingsData(resetSavings);
    setVisitedSteps(initialVisitedSteps);
    setHasSavedProgress(false); // No saved progress anymore - fresh start
    saveProgress(updates);
  };

  // Use a ref to track pending updates for batch operations
  const pendingUpdatesRef = useRef({});

  const updateJourneyData = (key, value) => {
    // Accumulate updates in ref to handle rapid sequential calls
    pendingUpdatesRef.current = { ...pendingUpdatesRef.current, [key]: value };
    const pendingUpdates = pendingUpdatesRef.current;
    
    // Use functional update to get latest state
    setJourneyData(prevData => {
      const newData = { ...prevData, ...pendingUpdates };
      
      // Clear pending updates after applying
      pendingUpdatesRef.current = {};
      
      // First meaningful input - show savings tracker
      if (!savingsData.visible && Object.keys(newData).length > 0) {
        const newSavings = { ...savingsData, visible: true };
        setSavingsData(newSavings);
        // Save to localStorage immediately with merged data
        const data = {
          currentPath,
          currentStep,
          journeyData: newData,
          savingsData: newSavings
        };
        localStorage.setItem('vindicated-journey', JSON.stringify(data));
      } else {
        // Save to localStorage immediately with merged data
        const data = {
          currentPath,
          currentStep,
          journeyData: newData,
          savingsData
        };
        localStorage.setItem('vindicated-journey', JSON.stringify(data));
      }
      
      return newData;
    });
  };

  const nextStep = () => {
    const newStep = currentStep + 1;
    const steps = getFlowSteps(currentPath);
    const newStepId = steps && steps[newStep] ? steps[newStep].id : null;
    
    // Add to visitedSteps if not already the last element
    let newVisitedSteps = [...visitedSteps];
    if (newStepId && newVisitedSteps[newVisitedSteps.length - 1] !== newStepId) {
      newVisitedSteps.push(newStepId);
    }
    
    setCurrentStep(newStep);
    setVisitedSteps(newVisitedSteps);
    saveProgress({ currentStep: newStep, visitedSteps: newVisitedSteps });
  };

  // Navigate to a specific step by ID (used for branching navigation)
  const goToStep = (stepId) => {
    const steps = getFlowSteps(currentPath);
    const targetIndex = steps ? steps.findIndex(s => s.id === stepId) : -1;
    
    if (targetIndex >= 0) {
      // Add to visitedSteps if not already the last element
      let newVisitedSteps = [...visitedSteps];
      if (newVisitedSteps[newVisitedSteps.length - 1] !== stepId) {
        newVisitedSteps.push(stepId);
      }
      
      setCurrentStep(targetIndex);
      setVisitedSteps(newVisitedSteps);
      saveProgress({ currentStep: targetIndex, visitedSteps: newVisitedSteps });
    }
  };

  // Helper to check if a step is valid given current journeyData
  const isStepValid = useCallback((stepId, data) => {
    const steps = getFlowSteps(currentPath);
    if (!steps) return false;
    
    const step = steps.find(s => s.id === stepId);
    if (!step) return false;
    
    // If step has no condition, it's always valid
    if (!step.condition) return true;
    
    // Check the condition with the provided data
    return step.condition(data);
  }, [currentPath]);

  // Go back using the visited steps history stack
  const prevStep = () => {
    const steps = getFlowSteps(currentPath);
    if (!steps || visitedSteps.length === 0) {
      // Fallback: go to first step or journey selection
      setCurrentStep(0);
      const firstStepId = steps && steps[0] ? steps[0].id : null;
      const newVisitedSteps = firstStepId ? [firstStepId] : [];
      setVisitedSteps(newVisitedSteps);
      saveProgress({ currentStep: 0, visitedSteps: newVisitedSteps });
      return;
    }

    // Get current step ID
    const currentStepId = steps[currentStep]?.id;
    let newVisitedSteps = [...visitedSteps];
    
    // Pop current step if it's the last in the stack
    if (newVisitedSteps[newVisitedSteps.length - 1] === currentStepId) {
      newVisitedSteps.pop();
    }
    
    // Find a valid previous step by walking back through the stack
    while (newVisitedSteps.length > 0) {
      const prevStepId = newVisitedSteps[newVisitedSteps.length - 1];
      
      // Check if this step is still valid given current journeyData
      if (isStepValid(prevStepId, journeyData)) {
        const prevIndex = steps.findIndex(s => s.id === prevStepId);
        if (prevIndex >= 0) {
          setCurrentStep(prevIndex);
          setVisitedSteps(newVisitedSteps);
          saveProgress({ currentStep: prevIndex, visitedSteps: newVisitedSteps });
          return;
        }
      }
      
      // This step is no longer valid, pop it and continue
      newVisitedSteps.pop();
    }
    
    // No valid previous step found - go to first valid step
    const firstValidStepIndex = steps.findIndex((step, idx) => {
      if (!step.condition) return true;
      return step.condition(journeyData);
    });
    
    const targetIndex = firstValidStepIndex >= 0 ? firstValidStepIndex : 0;
    const targetStepId = steps[targetIndex]?.id;
    const finalVisitedSteps = targetStepId ? [targetStepId] : [];
    
    setCurrentStep(targetIndex);
    setVisitedSteps(finalVisitedSteps);
    saveProgress({ currentStep: targetIndex, visitedSteps: finalVisitedSteps });
  };

  // Check if we're at the first step (used for determining whether Back goes to journey selection)
  const isAtFirstStep = () => {
    return visitedSteps.length <= 1;
  };

  const updateSavings = (amount, category, description) => {
    const newBreakdown = [...savingsData.breakdown];
    const existingIndex = newBreakdown.findIndex(item => item.category === category);
    
    if (existingIndex >= 0) {
      newBreakdown[existingIndex] = { category, amount, description };
    } else {
      newBreakdown.push({ category, amount, description });
    }
    
    const newTotal = newBreakdown.reduce((sum, item) => sum + item.amount, 0);
    
    const newSavings = {
      ...savingsData,
      total: newTotal,
      breakdown: newBreakdown
    };
    
    setSavingsData(newSavings);
    saveProgress({ savingsData: newSavings });
  };

  const showSavingsExpanded = () => {
    setSavingsData(prev => ({ ...prev, expanded: true, hasShownExpanded: true }));
  };

  const toggleSavingsExpanded = () => {
    setSavingsData(prev => ({ ...prev, expanded: !prev.expanded }));
  };

  const resetJourney = () => {
    setCurrentPath(null);
    setCurrentStep(0);
    setJourneyData({});
    setVisitedSteps([]);
    setSavingsData({
      total: 0,
      breakdown: [],
      visible: false,
      expanded: false,
      hasShownExpanded: false
    });
    setHasSavedProgress(false);
    try {
      localStorage.removeItem(JOURNEY_STORAGE_KEY);
      localStorage.removeItem('vindicated-journey');
    } catch (e) {
      // fail silently
    }
  };

  // Get the current step ID for display/navigation purposes
  const getCurrentStepId = () => {
    const steps = getFlowSteps(currentPath);
    return steps && steps[currentStep] ? steps[currentStep].id : null;
  };

  const getProgress = () => {
    if (!currentPath) return 0;
    // Each path has different number of steps (actual counts from journeyFlows.js)
    const pathSteps = {
      'buy': 19,  // Updated to include buy-full-journey-gate and trade-offers-organizer steps
      'lease': 8,
      'trade': 7,
      'learn': 5
    };
    const totalSteps = pathSteps[currentPath] || 10;
    return Math.min(100, Math.round((currentStep / totalSteps) * 100));
  };

  return (
    <JourneyContext.Provider value={{
      currentPath,
      currentStep,
      journeyData,
      savingsData,
      deviceId,
      visitedSteps,
      hasFullJourneyAccess,
      hasSavedProgress,
      entitlementLoading,
      entitlementError,
      refreshEntitlement,
      startPath,
      updateJourneyData,
      nextStep,
      prevStep,
      goToStep,
      isAtFirstStep,
      updateSavings,
      showSavingsExpanded,
      toggleSavingsExpanded,
      resetJourney,
      getProgress,
      getCurrentStepId
    }}>
      {children}
    </JourneyContext.Provider>
  );
};

export const useJourney = () => {
  const context = useContext(JourneyContext);
  if (!context) {
    throw new Error('useJourney must be used within JourneyProvider');
  }
  return context;
};